import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Save, X, Download } from 'lucide-react';
import { BASE_URL } from '../../../config/api';
import axios from 'axios';

const EnvVars = ({ projectId, appId, amplifyAppId, appName, appStatus }) => {
  const [envContent, setEnvContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditButton, setShowEditButton] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const parseEnvVarsToText = (envVarsArray) => {
    return envVarsArray.map(({ key, value }) => `${key}=${value}`).join('\n');
  };

  const parseTextToEnvVars = (text) => {
    const lines = text.split('\n');
    const envVarsObject = {};
    
    lines.forEach(line => {
      // Skip comments and empty lines
      if (line.trim() === '' || line.trim().startsWith('#')) {
        return;
      }
      
      // Find the first = character to split key and value
      const equalIndex = line.indexOf('=');
      if (equalIndex > 0) {
        const key = line.substring(0, equalIndex).trim();
        const value = line.substring(equalIndex + 1);  // Don't trim to preserve exact formatting
        if (key) {
          envVarsObject[key] = value;
        }
      }
    });
    
    return envVarsObject;
  };

  const fetchFrontendAppEnvVars = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let envVarsObject = {};
      if(appStatus === 'Active' && amplifyAppId) {
        const response = await axios.get(`https://cloudify-prod.wizr.in/api/amplify/apps/${amplifyAppId}/env-vars`);
        envVarsObject = response.data;
      } else if(appId) {
        const response = await axios.get(`https://cloudify-prod.wizr.in/${projectId}/frontend-app/${appId}/environment-variables`);
        envVarsObject = response.data.envVars || {};
      }
      
      // Convert to array of key-value pairs
      const envVarsArray = Object.entries(envVarsObject || {}).map(([key, value]) => ({
        key,
        value
      }));
      
      const textContent = parseEnvVarsToText(envVarsArray);
      setEnvContent(textContent);
      setOriginalContent(textContent);
    } catch (err) {
      console.error('Error fetching environment variables:', err);
      setError('Failed to fetch environment variables. Please try again.');
      setEnvContent('');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, appId, amplifyAppId, appStatus]);

  const saveEnvFile = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Convert text format to object
      const envVars = parseTextToEnvVars(envContent);

      // Update Amplify app environment variables
      if (appStatus === 'Active' && amplifyAppId) {
        await axios.put(`https://cloudify-prod.wizr.in/api/amplify/apps/${amplifyAppId}/env-vars`, envVars);
      }

      // Update environment variables in the frontend app
      await axios.put(`https://cloudify-prod.wizr.in/${projectId}/frontend-app/${appId}/environment-variables`, {envVars});

      setOriginalContent(envContent);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving environment variables:', err);
      setError('Failed to save environment variables. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const downloadEnvFile = () => {
    try {
      const blob = new Blob([envContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = '.env';
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading env file:', err);
      setError('Failed to download environment file.');
    }
  };

  useEffect(() => {
    fetchFrontendAppEnvVars();
  }, [fetchFrontendAppEnvVars]);

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Environment Variables</h2>
          <p className="text-gray-500 text-sm">
            Manage environment variables for your application
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <button
              onClick={downloadEnvFile}
              className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2"
              title="Download Environment File"
              disabled={!envContent.trim() || !amplifyAppId}
            >
              <Download className="h-4 w-4" />
              Download Env
            </button>
          ): null}
        </div>
      </div>

      {error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div 
          className="relative bg-gray-50 rounded-md border border-gray-200"
          onMouseEnter={() => !isEditing && setShowEditButton(true)}
          onMouseLeave={() => !isEditing && setShowEditButton(false)}
        >
          {isEditing ? (
            <textarea
              value={envContent}
              onChange={(e) => setEnvContent(e.target.value)}
              className="w-full h-[32rem] font-mono text-sm p-4 bg-white rounded-md border-0 focus:ring-2 focus:ring-indigo-500"
              spellCheck="false"
              placeholder="KEY=value
ANOTHER_KEY=another value"
            />
          ) : (
            <>
              <pre className="w-full h-[32rem] font-mono text-sm p-4 overflow-auto whitespace-pre">
                {envContent || 'No environment variables found. Click Edit to add some.'}
              </pre>
              {showEditButton && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2"
                  disabled={!amplifyAppId && appStatus !== 'Active'}
                >
                  <Edit className="h-5 w-5 text-gray-600" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {isEditing && (
        <div className="flex justify-end pt-4 gap-2">
          <button
            onClick={() => {
              setIsEditing(false);
              setEnvContent(originalContent);
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors mr-2"
          >
            Cancel
          </button>
          <button
            onClick={saveEnvFile}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {!isEditing && !envContent && !isLoading && !amplifyAppId && appStatus !== 'Active' && (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Amplify configuration required
            </h3>
            <p className="text-gray-600">
              Please provide an Amplify app ID or app name to manage environment variables
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvVars;